import { useEffect, useState } from 'react';
import { infaqService, Donation } from '../../../services/infaqService';
import LoadingSpinner from '../../../components/LoadingSpinner';

function InfaqDonationsPage() {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    status: '',
    donorName: '',
    donorEmail: '',
    campaignId: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDonations();
  }, [pagination.page, filters]);

  const fetchDonations = async () => {
    try {
      setLoading(true);
      const data = await infaqService.getAllDonations({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      setDonations(data.donations);
      setPagination(data.pagination);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat dermaan');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'Dibayar';
      case 'PENDING':
        return 'Menunggu';
      case 'FAILED':
        return 'Gagal';
      case 'CANCELLED':
        return 'Dibatalkan';
      default:
        return status;
    }
  };

  if (loading && donations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Pengurusan Dermaan</h1>
          <p className="text-gray-600">Lihat dan urus semua dermaan</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              >
                <option value="">Semua</option>
                <option value="PAID">Dibayar</option>
                <option value="PENDING">Menunggu</option>
                <option value="FAILED">Gagal</option>
                <option value="CANCELLED">Dibatalkan</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nama Penderma</label>
              <input
                type="text"
                value={filters.donorName}
                onChange={(e) => handleFilterChange('donorName', e.target.value)}
                placeholder="Cari nama..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-mel</label>
              <input
                type="email"
                value={filters.donorEmail}
                onChange={(e) => handleFilterChange('donorEmail', e.target.value)}
                placeholder="Cari e-mel..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ status: '', donorName: '', donorEmail: '', campaignId: '' })}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Donations Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">No. Rujukan</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Penderma</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Kempen</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Jumlah</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Tarikh</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {donations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Tiada dermaan dijumpai
                    </td>
                  </tr>
                ) : (
                  donations.map((donation) => (
                    <tr key={donation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{donation.transactionId}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {donation.isAnonymous ? 'Tanpa Nama' : donation.donorName}
                          </p>
                          <p className="text-xs text-gray-500">{donation.donorEmail}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{donation.campaign.title}</td>
                      <td className="px-6 py-4 text-right font-semibold text-[#0D7330]">
                        {infaqService.formatCurrency(donation.amount)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                            donation.status,
                          )}`}
                        >
                          {getStatusLabel(donation.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-600">
                        {new Date(donation.createdAt).toLocaleDateString('ms-MY')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Menunjuk {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} -
                {Math.min(pagination.page * pagination.limit, pagination.total)} daripada {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sebelum
                </button>
                <span className="text-sm text-gray-600">
                  Halaman {pagination.page} daripada {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Seterusnya
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InfaqDonationsPage;
